import {
  all,
  takeLatest,
  select,
  call,
  put,
  getContext,
  setContext,
  delay,
} from 'redux-saga/effects';
import { AxiosResponse } from 'axios';

import {
  getBillingClinic,
  getClinicDetails,
  getProfessionalDetails,
  updateProfessional,
  updateClinic,
  getZipCodeSaga,
  updateBillingClinic,
} from '../services';
import {
  getCFMUpdateData,
  setData,
  submitProfessional,
  requestPending,
  professionalUpdatedSuccess,
  dataUpdateError,
  clinicUpdatedSuccess,
  submitClinic,
  submitClinicBilling,
  clinicBillingUpdateSuccess,
  setStep,
  checkNextStep,
  setPossibleSteps,
} from '.';
import {
  Clinic,
  Professional,
  CFMUpdatePayload,
  ProfessionalFormValues,
  ClinicFormValues,
  UpdateClinicService,
  ClinicBillingType,
  CFMUpdateSteps,
} from '../types';
import { captureException } from 'shared/utils/handlerErrors';
import {
  isProfessionalIncomplete,
  isClinicIncomplete,
  isBillingIncomplete,
  customizeErrorMessage,
} from '../utils';
import {
  cboSelector,
  clinicBillingSelector,
  clinicBillingUpdateDoneSelector,
  clinicSelector,
  clinicUpdateDoneSelector,
  getUserInfo,
  professionalSelector,
  professionalUpdateDoneSelector,
} from './selectors';
import { CBO } from 'features/tuss/cbo/types';
import { listCBO } from 'features/tuss/cbo/state';
import { toIsoDate } from 'features/checkout/utils/dateUtils';
import { onlyNumbers } from 'shared/utils/formatters';
import USER_KIND from 'features/schedule/constants';
import { PERSON_KIND } from '../constants';

export function* getClinicData(clinicId: number) {
  try {
    const { data }: AxiosResponse<Clinic> = yield call(
      getClinicDetails,
      clinicId,
    );
    return data;
  } catch (error) {
    yield call(captureException, error);
  }
  return null;
}

export function* getBillingClinicData(subscriptionId: string) {
  try {
    const { data }: AxiosResponse<ClinicBillingType> = yield call(
      getBillingClinic,
      subscriptionId,
    );
    return data;
  } catch (error) {
    yield call(captureException, error);
  }
  return null;
}

export function* getProfessionalData(physicianId: string) {
  try {
    const { data }: AxiosResponse<Professional> = yield call(
      getProfessionalDetails,
      physicianId,
    );
    return data;
  } catch (error) {
    yield call(captureException, error);
  }
  return null;
}

export function buildPayload(
  clinic?: Clinic,
  professional?: Professional,
  clinicBilling?: ClinicBillingType,
): CFMUpdatePayload {
  return {
    clinic:
      clinic && isClinicIncomplete(clinic as unknown as Record<string, unknown>)
        ? clinic
        : null,
    professional:
      professional && isProfessionalIncomplete(professional)
        ? professional
        : null,
    clinicBilling:
      clinicBilling &&
      isBillingIncomplete(clinicBilling as unknown as Record<string, unknown>)
        ? clinicBilling
        : null,
  };
}

export function* getDataSaga(): Generator {
  if (process.env.NODE_ENV !== 'test') yield delay(1000);
  const {
    physicianId,
    isSubscriber,
    isClinicAdmin,
    isAdmin,
    clinicId,
    subscriptionId,
    userKind,
  } = yield select(getUserInfo);

  if (!isSubscriber) return;

  const { professional, clinic, clinicBilling } = yield all({
    professional:
      userKind !== USER_KIND.RECEPTIONIST
        ? call(getProfessionalData, physicianId)
        : null,
    clinic: isClinicAdmin ? call(getClinicData, clinicId) : null,
    clinicBilling: isAdmin ? call(getBillingClinicData, subscriptionId) : null,
  });

  const payload = buildPayload(clinic, professional, clinicBilling);

  if (payload.professional) {
    yield put(listCBO());
  }

  if (payload.clinic ?? payload.professional ?? payload.clinicBilling) {
    yield put(setData(payload));
  }
}

export function* submitProfessionalWorker({
  payload,
}: {
  payload: ProfessionalFormValues;
}) {
  yield put(requestPending());

  const { physicianId } = yield select(getUserInfo);

  const cboList: CBO[] = yield select(cboSelector);
  const cboNumber =
    cboList.find((item) => item.term === payload.cbo)?.cbo || payload.cbo;

  const requestPayload = {
    ...payload,
    cbo: cboNumber,
    professional_cnpj: payload.professional_cnpj,
  };

  try {
    yield call(updateProfessional, physicianId, requestPayload);
    yield put(professionalUpdatedSuccess());
    yield put(checkNextStep());
  } catch (error) {
    yield put(dataUpdateError(customizeErrorMessage(error)));
    yield call(captureException, error);
  }
}

export function* submitClinicWorker({
  payload,
}: {
  payload: ClinicFormValues;
}) {
  yield put(requestPending());

  const { clinicId } = yield select(getUserInfo);

  const requestPayload = {
    ...payload,
    clinic_cnpj: payload.clinic_cnpj,
  };

  try {
    const updateClinicService: UpdateClinicService = yield getContext(
      'updateClinic',
    );

    yield call(updateClinicService, clinicId, requestPayload);
    yield put(clinicUpdatedSuccess());
    yield put(checkNextStep());
  } catch (error) {
    yield put(dataUpdateError(customizeErrorMessage(error)));
    yield call(captureException, error);
  }
}

export function* submitBillingWorker({
  payload,
}: {
  payload: ClinicBillingType;
}) {
  yield put(requestPending());
  try {
    const { subscriptionId } = yield select(getUserInfo);

    const formattedPayload =
      payload.person_kind === PERSON_KIND.PF && payload.billing_birthdate
        ? {
            ...payload,
            billing_birthdate: toIsoDate(payload.billing_birthdate),
            cpf_cnpj: onlyNumbers(payload.cpf_cnpj),
          }
        : {
            ...payload,
            cpf_cnpj: onlyNumbers(payload.cpf_cnpj),
          };

    yield call(updateBillingClinic, subscriptionId, formattedPayload);
    yield put(clinicBillingUpdateSuccess());
    yield put(checkNextStep());
  } catch (error) {
    yield put(dataUpdateError(customizeErrorMessage(error)));
    yield call(captureException, error);
  }
}

export function* checkNextStepWorker() {
  const { isClinicAdmin, isAdmin, userKind } = yield select(getUserInfo);

  const professional: Professional = yield select(professionalSelector);
  const clinic: Clinic = yield select(clinicSelector);
  const clinicBilling: ClinicBillingType = yield select(clinicBillingSelector);
  const professionalUpdateDone: boolean = yield select(
    professionalUpdateDoneSelector,
  );
  const clinicUpdateDone: boolean = yield select(clinicUpdateDoneSelector);
  const clinicBillingUpdateDone: boolean = yield select(
    clinicBillingUpdateDoneSelector,
  );

  const stepOrder = Object.values(CFMUpdateSteps);
  const possibleSteps: CFMUpdateSteps[] = [];
  if (
    userKind !== USER_KIND.RECEPTIONIST &&
    professional &&
    !professionalUpdateDone
  ) {
    possibleSteps.push(CFMUpdateSteps.professional);
  }

  if (isClinicAdmin && clinic && !clinicUpdateDone) {
    possibleSteps.push(CFMUpdateSteps.clinic);
  }

  if (isAdmin && clinicBilling && !clinicBillingUpdateDone) {
    possibleSteps.push(CFMUpdateSteps.clinicBilling);
  }

  const nextStep = [...possibleSteps].sort(
    (a, b) => stepOrder.indexOf(a) - stepOrder.indexOf(b),
  )[0];

  yield put(setPossibleSteps(possibleSteps));
  if (nextStep) {
    yield put(setStep(nextStep));
  } else {
    yield put(setStep(CFMUpdateSteps.done));
  }
}

export default function* cfmUpdateSagas() {
  yield setContext({ updateClinic });

  yield all([
    takeLatest(getCFMUpdateData, getDataSaga),
    takeLatest(submitProfessional, submitProfessionalWorker),
    takeLatest(submitClinic, submitClinicWorker),
    takeLatest(submitClinicBilling, submitBillingWorker),
    takeLatest(checkNextStep, checkNextStepWorker),
    call(getZipCodeSaga),
  ]);
}
